import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { Loader2, Maximize2, X } from 'lucide-react';
import * as Sentry from '@sentry/react';
import mermaid from 'mermaid';

let mermaidInitialized = false;
let globalRenderCounter = 0;

// Module-level mutex that serializes every mermaid.parse + mermaid.render
// call across the whole app. Mermaid uses shared internal/global state
// (a single SVG layout context, a single id-keyed temp container) and is
// NOT safe to call concurrently — overlapping renders corrupt each other
// and produce spurious "render error" flashes even on perfectly valid
// syntax. Per-instance locks (useRef inside MermaidDiagram) do not catch
// this because React StrictMode double-mounts and multiple sibling diagrams
// each have their own ref.
let renderMutex: Promise<unknown> = Promise.resolve();

async function withRenderLock<T>(fn: () => Promise<T>): Promise<T> {
  const previous = renderMutex;
  let release: () => void = () => {};
  renderMutex = new Promise<void>((resolve) => {
    release = resolve;
  });
  try {
    // Wait for any prior render to fully settle (success OR failure)
    // before touching mermaid's globals.
    await previous.catch(() => {});
    return await fn();
  } finally {
    release();
  }
}

function ensureMermaidInit() {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'neutral',
    securityLevel: 'strict',
    // Its error SVG leaves nodes behind that corrupt later renders.
    suppressErrorRendering: true,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    flowchart: { useMaxWidth: true, htmlLabels: false, curve: 'basis' },
  });
  mermaidInitialized = true;
}

/* eslint-disable no-misleading-character-class */
const EMOJI_RE =
  /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{200D}\u{20E3}\u{FE0F}\u{E0020}-\u{E007F}]/gu;
/* eslint-enable no-misleading-character-class */

function stripEmojis(code: string): string {
  return code.replace(EMOJI_RE, '').replace(/ {2,}/g, ' ');
}

const SVG_SANITIZE_CONFIG = {
  USE_PROFILES: { svg: true, svgFilters: true, html: true },
  ADD_TAGS: ['foreignObject'],
  FORBID_CONTENTS: [
    'annotation-xml',
    'audio',
    'colgroup',
    'head',
    'iframe',
    'math',
    'mi',
    'mn',
    'mo',
    'ms',
    'mtext',
    'noembed',
    'noframes',
    'noscript',
    'plaintext',
    'script',
    'template',
    'thead',
    'video',
    'xmp',
  ],
  HTML_INTEGRATION_POINTS: {
    foreignobject: true,
    'annotation-xml': true,
  },
};

function sanitizeSvg(svg: string): string {
  return DOMPurify.sanitize(svg, SVG_SANITIZE_CONFIG);
}

/* ------------------------------------------------------------------ */
/* Fullscreen overlay                                                  */
/* ------------------------------------------------------------------ */

const DiagramOverlay: React.FC<{
  svgHtml: string;
  onClose: () => void;
}> = ({ svgHtml, onClose }) => {
  const sanitizedSvgHtml = useMemo(() => sanitizeSvg(svgHtml), [svgHtml]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal(
    <div
      className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm'
      onClick={onClose}
    >
      <div
        className='relative max-h-[90vh] w-[95vw] overflow-auto rounded-xl border border-border bg-white p-8 pt-12 shadow-2xl dark:bg-zinc-950'
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type='button'
          onClick={onClose}
          className='absolute right-3 top-3 z-10 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
        >
          <X className='size-5' />
        </button>
        <div
          className='flex items-center justify-center [&_svg]:h-auto [&_svg]:w-full [&_svg]:max-w-full'
          dangerouslySetInnerHTML={{ __html: sanitizedSvgHtml }}
        />
      </div>
    </div>,
    document.body,
  );
};

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

interface MermaidDiagramProps {
  code: string;
  className?: string;
  // When true, the surrounding markdown is still streaming. We defer
  // every render attempt until streaming finishes — partial-code parse
  // failures in the middle of a stream are the #1 source of error
  // flashes during a chat response.
  isStreaming?: boolean;
}

const DEBOUNCE_MS = 400;

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({
  code,
  className,
  isStreaming = false,
}) => {
  const [svgHtml, setSvgHtml] = useState<string | null>(null);
  const [visibleError, setVisibleError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const lastRenderedCodeRef = useRef<string>('');
  const latestCleanCodeRef = useRef<string>('');
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Shared by the render effect and the loading gate below: an empty fence
  // has nothing to draw, so it must not sit on the loading state.
  const cleanCode = useMemo(() => stripEmojis(code).trim(), [code]);

  const sanitizedSvgHtml = useMemo(
    () => (svgHtml ? sanitizeSvg(svgHtml) : null),
    [svgHtml],
  );

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current !== null) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);

  const renderDiagram = useCallback(
    async (cleanCode: string) => {
      globalRenderCounter++;
      const renderId = `mermaid_svg_${globalRenderCounter}`;

      try {
        // The retry sequence happens inside one lock hold so a queued
        // sibling render can't run between the failure and the retry.
        const { svg } = await withRenderLock(async () => {
          ensureMermaidInit();
          try {
            // render() parses internally; a separate parse() dirties it.
            return await mermaid.render(renderId, cleanCode);
          } catch (err) {
            // mermaid.render() resets its own config/parser state on every
            // call, so this isn't recovering from corrupted globals — it's
            // insurance against a flaky dynamic import in mermaid's lazy
            // per-diagram-type loader. Log the first failure since the
            // retry would otherwise make it invisible.
            Sentry.addBreadcrumb({
              category: 'mermaid-diagram-render',
              message: 'First render attempt failed, retrying',
              level: 'warning',
              data: { error: err instanceof Error ? err.message : String(err) },
            });
            return await mermaid.render(renderId, cleanCode);
          }
        });

        if (cleanCode === latestCleanCodeRef.current) {
          lastRenderedCodeRef.current = cleanCode;
          setSvgHtml(svg);
          clearErrorTimer();
          setVisibleError(null);
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            context: 'mermaid-diagram-render',
            codeLength: cleanCode.length,
          },
        });
        if (cleanCode === latestCleanCodeRef.current) {
          // Delay showing the error so transient parse failures during
          // streaming don't flash. If the code changes before the timer
          // fires, the error is suppressed.
          clearErrorTimer();
          const errorMsg =
            err instanceof Error ? err.message : 'Failed to render diagram';
          errorTimerRef.current = setTimeout(() => {
            setVisibleError(errorMsg);
          }, 1500);
        }
      }
    },
    [clearErrorTimer],
  );

  useEffect(() => {
    return clearErrorTimer;
  }, [clearErrorTimer]);

  useEffect(() => {
    if (!cleanCode) {
      latestCleanCodeRef.current = '';
      lastRenderedCodeRef.current = '';
      setSvgHtml(null);
      clearErrorTimer();
      setVisibleError(null);
      return;
    }

    latestCleanCodeRef.current = cleanCode;
    clearErrorTimer();
    setVisibleError(null);

    // While the surrounding chat message is still streaming, never
    // attempt a render. Partial mermaid code is almost always invalid,
    // and a failed parse in the middle of streaming is the most common
    // cause of the user-visible error flash.
    if (isStreaming) return;

    if (cleanCode === lastRenderedCodeRef.current) return;

    const timer = setTimeout(() => {
      renderDiagram(cleanCode);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [cleanCode, isStreaming, renderDiagram, clearErrorTimer]);

  // An empty fence (```mermaid with no body) has no render in flight and
  // never will, so there is nothing to wait for — showing the loading state
  // here is what left diagrams stuck on "Rendering diagram…" forever.
  if (!cleanCode) return null;

  if (visibleError && !sanitizedSvgHtml) {
    return (
      <div className='my-4 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30'>
        <p className='mb-2 text-xs font-medium text-red-600 dark:text-red-400'>
          Diagram render error
        </p>
        <pre className='overflow-x-auto whitespace-pre-wrap text-xs text-red-500 dark:text-red-400'>
          {code}
        </pre>
        <details className='mt-2'>
          <summary className='cursor-pointer text-[11px] text-red-500/70 hover:text-red-500 dark:text-red-400/70 dark:hover:text-red-400'>
            Error details
          </summary>
          <pre className='mt-1 overflow-x-auto whitespace-pre-wrap text-[11px] text-red-500/80 dark:text-red-400/80'>
            {visibleError}
          </pre>
        </details>
      </div>
    );
  }

  if (!sanitizedSvgHtml) {
    return (
      <div className='my-4 flex items-center justify-center rounded-md border border-border bg-white p-6 dark:bg-zinc-950'>
        <Loader2 className='size-5 animate-spin text-muted-foreground' />
        <span className='ml-2 text-xs text-muted-foreground'>
          Rendering diagram…
        </span>
      </div>
    );
  }

  return (
    <>
      <div
        className={`group/diagram relative my-4 overflow-x-auto rounded-md border border-border bg-white p-4 text-center dark:bg-zinc-950 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full ${className ?? ''}`}
      >
        <button
          type='button'
          onClick={() => setExpanded(true)}
          className='absolute right-2 top-2 rounded-md bg-white/80 p-1.5 text-muted-foreground opacity-0 shadow-sm transition-all hover:bg-white hover:text-foreground group-hover/diagram:opacity-100 dark:bg-zinc-900/80 dark:hover:bg-zinc-900'
          title='Expand diagram'
        >
          <Maximize2 className='size-4' />
        </button>
        <div dangerouslySetInnerHTML={{ __html: sanitizedSvgHtml }} />
      </div>

      {expanded && (
        <DiagramOverlay
          svgHtml={sanitizedSvgHtml}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
};

export default MermaidDiagram;
